import './KpiSegmentInput.scss';

import { CaretPositionChangeType, KpiSegmentInputProps } from '@deckManagement/types/CalculatedKPIBuilder.types';
import { Chip, Input, InputProps } from '@mui/material';
import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useRef } from 'react';

/**
 * Based on the MUI's <Input> component, this component has auto-width functionality (based on the input's value).
 * Additionally, because this component is intended to be used grouped together with other instances of the component,
 * callbacks can be provided for when the caret position needs to move between multiple components, allowing the parent
 * component to manage the input focus and caret position as needed.
 *
 * The objective of this component, when used with other instances of this component, is to allow the user to input
 * a KPI segment value as if it were a single Input field, while allowing stylizing to occur on a per-segment
 * basis.
 */
const KpiSegmentInput = (props: KpiSegmentInputProps) => {
  const {
    onCaretPositionChangeRequest,
    onAddSegmentRequest,
    value = '',
    dimSumNode,
    onKeyDown,
    onValueChange,
    onFocusChange,
    handleBlur,
    handleDetachNode,
    segmentIndex,
    inputRef: forwardedRef,
    ...rest
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  /**
   * Handle keyboard events to determine if certain callback props should be called based on certain conditions.
   *
   * @param event - The keyboard event to handle
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      const input = event.target as HTMLInputElement;
      const { selectionStart, selectionEnd } = input;

      const deleteKey = event.key === 'Backspace';

      if ((event.key === 'ArrowLeft' || event.key === 'Backspace') && selectionStart === 0 && selectionEnd === 0) {
        // Handles request to move to the previous input when the caret position is at the beginning of the input
        // and the user presses the left arrow key or backspace key
        event.preventDefault();
        onCaretPositionChangeRequest?.(segmentIndex, CaretPositionChangeType.Previous, deleteKey);
      } else if (event.key === 'ArrowRight' && selectionStart === input.value.length) {
        // Handles request to move the next input when the caret position is at the end of the input
        // and the user presses the right arrow key
        event.preventDefault();
        onCaretPositionChangeRequest?.(segmentIndex, CaretPositionChangeType.Next, deleteKey);
      } else if (event.key === 'Home' || (event.key === 'ArrowUp' && event.getModifierState('Meta'))) {
        // Handles request to move to the beginning of the parent component's input list when the user presses the
        // home key or the command key + up arrow key
        event.preventDefault();
        onCaretPositionChangeRequest?.(segmentIndex, CaretPositionChangeType.Beginning, deleteKey);
      } else if (event.key === 'End' || (event.key === 'ArrowDown' && event.getModifierState('Meta'))) {
        // Handles request to move to the end of the parent component's input list when the user presses the
        // end key or the command key + down arrow key
        event.preventDefault();
        onCaretPositionChangeRequest?.(segmentIndex, CaretPositionChangeType.End, deleteKey);
      } else if (event.key === 'Enter' && event.getModifierState('Shift')) {
        // Handles request to add a new segment when the user presses the shift + enter key
        event.preventDefault();
        onAddSegmentRequest?.(segmentIndex);
      } else {
        // Otherwise, pass the event to the parent component's `onKeyDown` callback.
        onKeyDown?.(segmentIndex, event);
      }
    },
    [onAddSegmentRequest, onCaretPositionChangeRequest, onKeyDown, segmentIndex],
  );

  /**
   * Handles calling the parent component's `onFocusChange` callback when this component gains focus.
   */
  const handleFocus = useCallback(() => {
    onFocusChange?.(segmentIndex);
  }, [onFocusChange, segmentIndex]);

  /**
   * Handles calling the parent component's `onValueChange` callback when the input value changes.
   *
   * @param e - The change event or the new value
   */
  const handleValueChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | string) => {
      const newValue = typeof e === 'string' ? e : e.target.value;
      // setValue(newValue);
      onValueChange?.(segmentIndex, newValue);
    },
    [onValueChange, segmentIndex],
  );

  /**
   * Handles calculating the width of the input field when the value changes.
   */
  useEffect(() => {
    if (measureRef.current) {
      const width = measureRef.current.offsetWidth;
      if (inputRef.current) {
        inputRef.current.style.width = rest.fullWidth ? '100%' : `${Math.max(width, 5)}px`;
      }
    }
  }, [dimSumNode, inputRef, rest.fullWidth, value]);

  /**
   * Renders the input field and a hidden span object which is used to measure the needed width of the input field.
   *
   * @returns The rendered input field
   */
  const renderInput = useCallback(() => {
    // hidden <span> for measuring the width of the value
    const spanValue = (
      <span
        ref={measureRef}
        style={{
          // TODO: CSS Class
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'pre',
          fontSize: 'inherit',
          fontFamily: 'inherit',
          paddingLeft: '4px',
          paddingRight: '4px',
        }}
      >
        {value || ' '}
      </span>
    );

    const commonInputProps: InputProps = {
      ref: inputRef,
      onKeyDown: handleKeyDown,
      onFocus: handleFocus,
      onBlur: () => handleBlur?.(segmentIndex),
      // Necessary to prevent the parent component from receiving click events when user clicks on this component.
      onClick: (e) => e.stopPropagation(),
    };

    const inputComponent = (
      <Input
        id={`kpiSegmentInput-${segmentIndex}`}
        {...rest}
        {...commonInputProps}
        value={value}
        onChange={handleValueChange}
        inputRef={forwardedRef}
        autoComplete='off'
      />
    );

    return (
      <>
        {spanValue}
        {inputComponent}
      </>
    );
  }, [forwardedRef, handleBlur, handleFocus, handleKeyDown, handleValueChange, rest, segmentIndex, value]);

  /**
   * Renders a chip with the dimension name and a delete button.
   *
   * @returns The rendered chip
   */
  const renderChip = useCallback(() => {
    return (
      <Chip
        className='kpiSegmentInput-chip'
        label={dimSumNode?.attributes.name}
        onDelete={() => handleDetachNode?.(segmentIndex)}
        size='small'
        color='primary'
        variant='filled'
        onClick={(e) => e.stopPropagation()}
      />
    );
  }, [dimSumNode, handleDetachNode, segmentIndex]);

  // Render the Input field, or (when a dimSumNode is attached) a Chip.
  return dimSumNode === undefined ? renderInput() : renderChip();
};

export default KpiSegmentInput;
